<script setup lang="ts">
import * as echarts from 'echarts'
import { onMounted, onUnmounted, reactive, ref } from 'vue'

// 页面配置
definePage({
  name: 'fund-flow',
  meta: {
    title: '行情',
  },
})

// 时间选择器选项
const timeOptions = [
  { label: '实时', value: 'realtime' },
  { label: '近1日', value: '1d' },
  { label: '近5日', value: '5d' },
  { label: '近20日', value: '20d' },
]

// 当前选中的时间
const selectedTime = ref('realtime')

// 行业排名数据
const industryRanking = reactive([
  {
    industry: '银行',
    netFlow: '+7.87亿',
    change: '-0.02%',
    leadingStock: '汇丰控股',
  },
  {
    industry: '科技',
    netFlow: '+5.42亿',
    change: '+1.25%',
    leadingStock: '腾讯控股',
  },
  {
    industry: '电讯',
    netFlow: '-3.14亿',
    change: '-0.56%',
    leadingStock: '中国移动',
  },
  {
    industry: '能源',
    netFlow: '+4.21亿',
    change: '+0.83%',
    leadingStock: '中石油',
  },
  {
    industry: '医疗',
    netFlow: '+2.98亿',
    change: '+0.32%',
    leadingStock: '药明生物',
  },
  {
    industry: '消费',
    netFlow: '-1.76亿',
    change: '-0.21%',
    leadingStock: '美团',
  },
  {
    industry: '地产',
    netFlow: '-2.34亿',
    change: '-0.45%',
    leadingStock: '万科企业',
  },
  {
    industry: '金融',
    netFlow: '+6.53亿',
    change: '+0.67%',
    leadingStock: '中国平安',
  },
])

// ECharts 实例
const chartRef = ref<HTMLDivElement | null>(null)
let chartInstance: echarts.ECharts | null = null

// 初始化图表
function initChart() {
  if (!chartRef.value) {
    return
  }

  // 创建ECharts实例
  chartInstance = echarts.init(chartRef.value)

  // 配置项 - 使用treemap展示多个矩形块，铺满整个容器
  const option = {
    tooltip: {
      trigger: 'item',
      formatter(params: any) {
        return `${params.name}<br />净流入: ${params.value}亿`
      },
    },
    series: [
      {
        type: 'treemap',

        // 顶层数据，每个行业一个块
        data: [
          {
            name: '银行',
            value: 7.87,
            itemStyle: {
              color: '#8C000D', // 红色表示资金流入
            },
          },
          {
            name: '电讯',
            value: 3.14,
            itemStyle: {
              color: '#00613C ', // 绿色表示资金流出
            },
          },
          {
            name: '科技',
            value: 5.23,
            itemStyle: {
              color: '#8C000D',
            },
          },
          {
            name: '地产',
            value: 2.15,
            itemStyle: {
              color: '#00613C ',
            },
          },
          {
            name: '能源',
            value: 4.32,
            itemStyle: {
              color: '#8C000D',
            },
          },
          {
            name: '医疗',
            value: 3.89,
            itemStyle: {
              color: '#8C000D',
            },
          },
          {
            name: '消费',
            value: 2.76,
            itemStyle: {
              color: '#00613C ',
            },
          },
          {
            name: '金融',
            value: 6.45,
            itemStyle: {
              color: '#8C000D',
            },
          },
        ],
        label: {
          show: true,
          fontSize: 12,
          color: '#fff',
          formatter: '{b}\n{c}亿',
        },
        upperLabel: {
          show: false, // 隐藏上层标签
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 0,
          gapWidth: 0, // 增加间隙使块之间有分隔
        },
        levels: [
          // 只保留第一层，不显示子层级
          {
            itemStyle: {
              borderColor: '#fff',
              borderWidth: 0,
              gapWidth: 0,
            },
            upperLabel: {
              show: false,
            },
          },
        ],
        // 铺满容器的设置
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        // 布局方向
        sort: undefined, // 使用默认排序
        // 确保填充整个容器
        nodeClick: false, // 禁用节点点击，避免进入下钻
        roam: false,
        labelLayout: {
          hideOverlap: true, // 隐藏重叠的标签
          draggable: false,
        },
        breadcrumb: {
          show: false,

        },
      },
    ],
    // 全局背景色
    backgroundColor: 'transparent',
  }

  chartInstance.setOption(option)

  // 监听窗口大小变化，调整图表大小
  window.addEventListener('resize', () => {
    if (chartInstance) {
      chartInstance.resize()
    }
  })
}

// 组件挂载后初始化图表
onMounted(() => {
  initChart()
})

// 组件卸载前清理
onUnmounted(() => {
  if (chartInstance) {
    chartInstance.dispose()
  }
})
// / 添加一个函数来根据时间选项获取数据
function getDataByTime(time: string) {
  // 根据不同时间选项返回不同数据
  const dataMap = {
    'realtime': [
      { name: '银行', value: 7.87, itemStyle: { color: '#8C000D' } },
      { name: '电讯', value: 3.14, itemStyle: { color: '#00613C ' } },
      { name: '科技', value: 5.23, itemStyle: { color: '#8C000D' } },
      { name: '地产', value: 2.15, itemStyle: { color: '#00613C ' } },
      { name: '能源', value: 4.32, itemStyle: { color: '#8C000D' } },
      { name: '医疗', value: 3.89, itemStyle: { color: '#8C000D' } },
      { name: '消费', value: 2.76, itemStyle: { color: '#00613C ' } },
      { name: '金融', value: 6.45, itemStyle: { color: '#8C000D' } },
    ],
    '1d': [
      { name: '银行', value: 5.23, itemStyle: { color: '#8C000D' } },
      { name: '电讯', value: 4.56, itemStyle: { color: '#00613C ' } },
      { name: '科技', value: 3.89, itemStyle: { color: '#8C000D' } },
      { name: '地产', value: 1.98, itemStyle: { color: '#00613C ' } },
      { name: '能源', value: 6.45, itemStyle: { color: '#00613C ' } },
      { name: '医疗', value: 2.76, itemStyle: { color: '#8C000D' } },
      { name: '消费', value: 5.12, itemStyle: { color: '#8C000D' } },
      { name: '制造', value: 3.45, itemStyle: { color: '#00613C ' } },
    ],
    '5d': [
      { name: '银行', value: 8.91, itemStyle: { color: '#8C000D' } },
      { name: '电讯', value: 2.34, itemStyle: { color: '#00613C ' } },
      { name: '科技', value: 7.65, itemStyle: { color: '#8C000D' } },
      { name: '地产', value: 3.21, itemStyle: { color: '#00613C ' } },
      { name: '能源', value: 5.78, itemStyle: { color: '#00613C ' } },
      { name: '医疗', value: 4.32, itemStyle: { color: '#8C000D' } },
      { name: '消费', value: 6.54, itemStyle: { color: '#8C000D' } },
      { name: '制造', value: 2.87, itemStyle: { color: '#00613C ' } },
    ],
    '20d': [
      { name: '银行', value: 12.34, itemStyle: { color: '#8C000D' } },
      { name: '电讯', value: 5.67, itemStyle: { color: '#00613C ' } },
      { name: '科技', value: 10.11, itemStyle: { color: '#8C000D' } },
      { name: '地产', value: 4.56, itemStyle: { color: '#00613C ' } },
      { name: '能源', value: 8.90, itemStyle: { color: '#00613C ' } },
      { name: '医疗', value: 7.89, itemStyle: { color: '#8C000D' } },
      { name: '消费', value: 9.01, itemStyle: { color: '#8C000D' } },
      { name: '制造', value: 6.34, itemStyle: { color: '#00613C ' } },
    ],
  }

  return dataMap[time as keyof typeof dataMap] || dataMap.realtime
}
// / 更新图表函数
function updateChart() {
  if (!chartInstance) {
    return
  }

  // 根据当前时间选项获取数据
  const currentData = getDataByTime(selectedTime.value)

  // 更新图表数据
  chartInstance.setOption({
    series: [{
      type: 'treemap',
      data: currentData,
    }],
  })
}

// 监听时间选项变化
watch(selectedTime, () => {
  updateChart()
})
</script>

<template>
  <div class="fund-flow-page">
    <!-- 顶部导航 -->
    <div class="page-header">
      <div class="header-left">
        <button class="back-btn" @click="$router.back()">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M15 19L9 13L15 7" stroke="currentColor" stroke-width="2" stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>
      <div class="header-title">
        资金流向-港股通
      </div>
      <div class="header-right" />
    </div>

    <!-- 提示信息 -->
    <div class="notice-bar">
      港股行情至少延时15分钟，购买港股行情
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 行业资金流向标题 -->
      <div class="section-header titile">
        <span>行业资金流向</span>
        <div class="section-actions">
          <button class="info-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" />
              <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </button>
          <button class="share-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path
                d="M4 12H20M20 12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12Z"
                stroke="currentColor" stroke-width="2"
              />
            </svg>
          </button>
        </div>
      </div>

      <!-- 时间选择器 -->
      <div class="time-filter">
        <button
          v-for="option in timeOptions" :key="option.value" class="time-option"
          :class="[{ active: selectedTime === option.value }]" @click="selectedTime = option.value"
        >
          {{ option.label }}
        </button>
      </div>

      <!-- 资金流向图表 -->
      <div class="flow-chart">
        <div ref="chartRef" class="chart-container" />
      </div>

      <!-- 行业排名 -->
      <div class="section-header">
        <span>行业排名</span>
      </div>

      <!-- <div class="ranking-table">
        <div class="table-header">
          <div class="header-cell">
            行业
          </div>
          <div class="header-cell">
            净流入
          </div>
          <div class="header-cell">
            涨跌幅
          </div>
          <div class="header-cell">
            领涨股
          </div>
        </div>

        <div class="table-body">
          <div v-for="(item, index) in industryRanking" :key="index" class="table-row">
            <div class="cell">
              {{ item.industry }}
            </div>
            <div class="cell" :class="{ positive: item.netFlow.startsWith('+') }">
              {{ item.netFlow }}
            </div>
            <div class="cell" :class="{ negative: item.change.startsWith('-') }">
              {{ item.change }}
            </div>
            <div class="cell">
              {{ item.leadingStock }}
            </div>
          </div>
        </div>
      </div> -->
      <div class="ranking-table">
        <vxe-table
          header-row-class-name="table-header" :data="industryRanking" stripe border
        >
          <vxe-column field="industry" title="行业" width="24%" />
          <vxe-column field="netFlow" title="净流入" width="24%" sortable>
            <template #default="{ row }">
              <span :class="row.netFlow.startsWith('+') ? 'up' : 'down'">
                {{ row.netFlow }}
              </span>
            </template>
          </vxe-column>
          <vxe-column field="change" title="涨跌幅" width="24%" sortable>
            <template #default="{ row }">
              <span :class="row.change.startsWith('+') ? 'up' : 'down'">
                {{ row.change }}
              </span>
            </template>
          </vxe-column>
          <vxe-column field="leadingStock" title="领涨股" width="24%" sortable />
        </vxe-table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fund-flow-page {
  min-height: 100vh;
  background-color: #f5f5f5;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0;
  color: white;
  background-color: #007aff;
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
}

.back-btn {
  padding: 8px;
  color: white;
  cursor: pointer;
  background: none;
  border: none;
}

.notice-bar {
  padding: 8px 16px;
  margin-bottom: 16px;
  font-size: 12px;
  color: #999;
  text-align: center;
  background-color: #fff9e6;
}

.main-content {
  padding: 0 16px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 8px;
  margin-bottom: 16px;
  border-bottom: 1px solid #eee;
}

.titile {
  justify-content: flex-start;
}

.section-actions {
  display: flex;
  gap: 0;
}

.info-btn,
.share-btn {
  padding: 8px;
  color: #999;
  cursor: pointer;
  background: none;
  border: none;
}

.time-filter {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 16px;
}

.time-option {
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 2px;
}

.time-option.active {
  color: white;
  background-color: #007aff;
  border-color: #007aff;
}

.flow-chart {
  margin-bottom: 24px;
}

.chart-container {
  width: 100%;
  height: 300px;
  overflow: hidden;
  background-color: white;
  border-radius: 8px;
}

.ranking-table {
  margin: 10px 0;

  /* min-height: 100vh; */

  /* overflow: hidden; */
  background-color: white;
  border-radius: 8px;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  padding: 12px 16px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #eee;
}

.header-cell {
  font-size: 14px;
  font-weight: 500;
  color: #666;
  text-align: left;
}

.table-body {
  /* display: grid; */
  grid-template-columns: 1fr 1fr 1fr 1fr;
}

.table-row {
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
}

.cell {
  width: 24%;
  font-size: 14px;
  color: #333;
  text-align: left;
}

.cell.positive {
  color: #8c000d;
}

.cell.negative {
  color: #2a9d8f;
}

.up {
  color: #f56c6c;
}

.down {
  color: #67c23a;
}
</style>
